import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CreatorEventsPanel from '../components/CreatorEventsPanel';
import EventForm from '../components/EventForm';
import MatchesPanel from '../components/MatchesPanel';
import NotificationsPanel from '../components/NotificationsPanel';
import { useAuth } from '../context/AuthContext';
import { Event, EventCatalogs } from '../types';
import { mockBackend } from '../services/mockBackend';
import { userFacingError } from '../utils/errors';

type PortalTab = 'form' | 'events' | 'matches' | 'notifications';

const tabOptions: Array<{ label: string; value: PortalTab }> = [
  { label: 'Enviar evento', value: 'form' },
  { label: 'Meus envios', value: 'events' },
  { label: 'Matches', value: 'matches' },
  { label: 'Notificações', value: 'notifications' },
];

const Portal = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalogs, setCatalogs] = useState<EventCatalogs>({ institutions: [], audiences: [] });
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [catalogError, setCatalogError] = useState('');
  const activeTab = (searchParams.get('tab') as PortalTab | null) || 'form';

  useEffect(() => {
    setCatalogError('');
    mockBackend.events
      .getCatalogs()
      .then(setCatalogs)
      .catch((loadError) =>
        setCatalogError(userFacingError(loadError, 'Não foi possível carregar sugestões de instituição e público.'))
      );
  }, [refreshToken]);

  const setTab = (tab: PortalTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next);
  };

  const handleActionComplete = () => {
    setRefreshToken((current) => current + 1);
  };

  if (!user) return null;

  return (
    <div className="portal-page">
      <section className="hero-section py-4 position-relative overflow-hidden">
        <div className="hero-blur" />
        <div className="container py-4">
          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
            <div>
              <span className="badge bg-brand text-white fw-semibold px-3 py-2 rounded-pill mb-3">
                Portal do Solicitante
              </span>
              <h1 className="text-white mb-2">Envie e acompanhe seus eventos</h1>
              <p className="text-white-50 mb-0">
                Centralize os pedidos que você enviou para a agenda de inovação e acompanhe o status
                de cada evento.
              </p>
            </div>
            <div className="text-end">
              <div className="text-white fw-semibold">{user.name}</div>
              <div className="text-white-50 small">{user.email}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 section-contrast">
        <div className="container">
          <div className="tab-strip mb-4">
            {tabOptions.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`btn ${activeTab === tab.value ? 'btn-brand' : 'btn-outline-light'}`}
                onClick={() => setTab(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {catalogError && <div className="alert alert-warning py-2">{catalogError}</div>}

          {activeTab === 'form' && (
            <EventForm
              userId={user.id}
              catalogs={catalogs}
	              initialEvent={editingEvent}
	              onSaved={() => {
	                setEditingEvent(null);
	                handleActionComplete();
	                setTab('events');
	              }}
              onCancelEdit={() => {
                setEditingEvent(null);
                setTab('events');
              }}
            />
          )}

          {activeTab === 'events' && (
            <CreatorEventsPanel
              userId={user.id}
              refreshToken={refreshToken}
              onActionComplete={handleActionComplete}
              onEditEvent={(event) => {
                setEditingEvent(event);
                setTab('form');
              }}
            />
          )}

          {activeTab === 'matches' && (
            <MatchesPanel
              userId={user.id}
              refreshToken={refreshToken}
              onActionComplete={handleActionComplete}
            />
          )}

          {activeTab === 'notifications' && (
            <NotificationsPanel userId={user.id} refreshToken={refreshToken} />
          )}
        </div>
      </section>
    </div>
  );
};

export default Portal;
